import { useCallback, useEffect, useState } from 'react'
import {
  ActionIcon,
  Avatar,
  Box,
  Button,
  Divider,
  Group,
  Menu,
  Popover,
  ScrollArea,
  Stack,
  Text,
  Tooltip,
  UnstyledButton,
} from '@mantine/core'
import { openConfirmModal } from '@mantine/modals'
import { IconClock, IconCoffee, IconLogout, IconUser, IconUsers } from '@/components/icons'
import { api, ApiError } from '@/lib/api'
import type { ColleaguesTodayResponse, MeUser, ShiftColleagueFactRow } from '@/types/api'
import { useSidebarShift } from '@/hooks/useSidebarShift'
import { useWorkspaceTabs } from '@/lib/workspace-tabs-context'
import { getRoleLabel } from '@/lib/role-label'
import { resolveUploadsUrl } from '@/lib/uploads'
import { resolveOperatorAvatarSrc } from '@/lib/dashboard-ai-operator'
import { formatShiftStartedAtForDisplay, formatUserTimeFromIso } from '@/lib/user-datetime-format'

function displayName(u: MeUser) {
  const parts = [u.first_name, u.last_name].filter(Boolean) as string[]
  if (parts.length) return parts.join(' ')
  return u.first_name || u.email || 'Оператор'
}

function initialsFromUser(u: MeUser) {
  const a = u.first_name?.trim()?.[0]
  const b = u.last_name?.trim()?.[0]
  const c = u.email?.trim()?.[0]
  const s = (a && b ? `${a}${b}` : a || b || c || 'О').toUpperCase()
  return s.slice(0, 2)
}

type Props = {
  user: MeUser
  onSignOut: () => void
}

function planShiftSubtitle(row: ShiftColleagueFactRow): string | null {
  const label = (row.planned_intervals_label || '').trim()
  const end = row.planned_end_local?.trim()
  if (label) return `Плановая смена: ${label}`
  if (end) return `Плановая смена до ${end}`
  return null
}

function ColleagueOnShiftLine({ row }: { row: ShiftColleagueFactRow }) {
  const src = resolveOperatorAvatarSrc({
    operator_name: [row.first_name, row.last_name].filter(Boolean).join(' ').trim() || row.email,
    operator_email: row.email,
    avatar_url: row.avatar_url,
  })
  const started = formatShiftStartedAtForDisplay(row.shift_started_at)
  const breakAt = row.is_on_break ? formatUserTimeFromIso(row.break_started_at) : null
  const factParts = [
    started ? `на смене с ${started}` : 'на смене',
    row.is_on_break ? (breakAt ? `перерыв с ${breakAt}` : 'перерыв') : null,
  ].filter(Boolean)
  const factLine = factParts.join(' · ')
  const planLine = planShiftSubtitle(row)

  return (
    <Group gap={8} wrap="nowrap" align="center" py={4} style={{ minWidth: 0 }}>
      <Avatar src={src} size={28} radius="xl">
        {(row.display_name || '?').charAt(0)}
      </Avatar>
      <Box style={{ flex: 1, minWidth: 0 }}>
        <Text size="sm" fw={500} lineClamp={1}>
          {row.display_name || '—'}
        </Text>
        <Text size="xs" c="dimmed" lineClamp={2}>
          {factLine}
        </Text>
        {planLine ? (
          <Text size="xs" c="dimmed" opacity={0.88} lineClamp={2} mt={2}>
            {planLine}
          </Text>
        ) : null}
      </Box>
    </Group>
  )
}

export function HeaderUserToolbar({ user, onSignOut }: Props) {
  const { openOrSelectTab } = useWorkspaceTabs()

  const [colleaguesLoading, setColleaguesLoading] = useState(false)
  const [colleaguesData, setColleaguesData] = useState<ColleaguesTodayResponse | null>(null)
  const [colleaguesErr, setColleaguesErr] = useState<string | null>(null)

  const loadColleaguesToday = useCallback(async () => {
    setColleaguesLoading(true)
    setColleaguesErr(null)
    try {
      const res = await api.scheduleColleaguesToday()
      setColleaguesData(res)
    } catch (e) {
      setColleaguesData(null)
      setColleaguesErr(e instanceof ApiError ? e.message : 'Не удалось загрузить')
    } finally {
      setColleaguesLoading(false)
    }
  }, [])

  const {
    shift,
    shiftInfo,
    planPrimary,
    planDetail,
    shiftBackend,
    busy,
    showBreak,
    breakLabel,
    breakInfo,
    isOnBreak,
    toggleShift,
    toggleBreak,
  } = useSidebarShift()

  const [avatarBroken, setAvatarBroken] = useState(false)
  useEffect(() => {
    setAvatarBroken(false)
  }, [user.avatar_url])

  const avatarSrc =
    user.avatar_url && !avatarBroken ? resolveUploadsUrl(user.avatar_url) : undefined
  const custom = user.custom_role?.trim()
  const roleLine1 = custom || getRoleLabel(user.role)
  const roleLine2 = custom ? getRoleLabel(user.role) : null

  const shiftTooltip = [
    shiftInfo,
    planPrimary || undefined,
    planDetail || undefined,
    breakInfo || undefined,
  ]
    .filter(Boolean)
    .join('\n')

  return (
    <Group gap={6} wrap="nowrap" align="center" justify="flex-end" style={{ minWidth: 0 }}>
      <Box visibleFrom="sm" style={{ minWidth: 0 }}>
        <Tooltip label={shiftTooltip} position="bottom" withArrow multiline maw={300}>
          <Text size="xs" c="dimmed" truncate maw={160} lh={1.3} style={{ cursor: 'default' }}>
            {shiftInfo}
          </Text>
        </Tooltip>
      </Box>
      {breakInfo ? (
        <Box visibleFrom="md" style={{ minWidth: 0 }}>
          <Text size="xs" c="dimmed" truncate maw={100} lh={1.3}>
            {breakInfo}
          </Text>
        </Box>
      ) : null}

      <Tooltip label={shiftTooltip} position="bottom" withArrow multiline maw={300}>
        <Button
          size="compact-xs"
          variant={shift.active ? 'light' : 'filled'}
          color={shift.active ? 'red' : 'brand'}
          leftSection={<IconClock size={14} stroke={1.5} />}
          loading={busy}
          onClick={() => void toggleShift()}
        >
          {shift.active ? 'Закончить смену' : 'Начать смену'}
        </Button>
      </Tooltip>

      {showBreak ? (
        <Tooltip label={breakInfo || breakLabel} position="bottom" withArrow>
          <Button
            size="compact-xs"
            variant="light"
            color={isOnBreak ? 'red' : 'gray'}
            leftSection={<IconCoffee size={14} stroke={1.5} />}
            loading={busy}
            onClick={() => void toggleBreak()}
          >
            {breakLabel}
          </Button>
        </Tooltip>
      ) : null}

      <Divider orientation="vertical" style={{ alignSelf: 'stretch' }} my={6} />

      <Popover
        width={300}
        position="bottom-end"
        withArrow
        shadow="md"
        withinPortal
        closeOnClickOutside
        onOpen={() => void loadColleaguesToday()}
      >
        <Popover.Target>
          <Tooltip label="Кто на смене" position="bottom" withArrow>
            <ActionIcon variant="default" size="sm" radius="md" aria-label="Кто на смене">
              <IconUsers size={18} stroke={1.5} />
            </ActionIcon>
          </Tooltip>
        </Popover.Target>
        <Popover.Dropdown p="sm">
            <Text size="xs" fw={600} mb={6}>
              Сейчас на смене
            </Text>
            {colleaguesData ? (
              <Text size="xs" c="dimmed" mb="xs">
                {colleaguesData.today_date} ·{' '}
                {colleaguesData.schedule_timezone ?? colleaguesData.workspace_timezone}
              </Text>
            ) : null}
            {colleaguesLoading ? (
              <Text size="xs" c="dimmed">
                Загрузка…
              </Text>
            ) : colleaguesErr ? (
              <Text size="xs" c="orange">
                {colleaguesErr}
              </Text>
            ) : (
              <ScrollArea.Autosize mah={280} type="auto">
                <Stack gap={0}>
                  {(() => {
                    const onFactAll = colleaguesData?.on_shift_fact ?? []
                    const onFact = onFactAll.filter((r) => r.user_id !== user.id)
                    return (
                      <>
                        {shift.active ? (
                          <>
                            <Text size="xs" c="dimmed" mb={4}>
                              Вы
                            </Text>
                            <Group gap={8} wrap="nowrap" align="center" py={4} style={{ minWidth: 0 }}>
                              <Avatar
                                src={
                                  user.avatar_url ? resolveUploadsUrl(user.avatar_url) : undefined
                                }
                                size={28}
                                radius="xl"
                              >
                                {initialsFromUser(user)}
                              </Avatar>
                              <Box style={{ flex: 1, minWidth: 0 }}>
                                <Text size="sm" fw={500} lineClamp={1}>
                                  {displayName(user)}
                                </Text>
                                <Text size="xs" c="dimmed" lineClamp={2}>
                                  {shiftInfo}
                                </Text>
                                {planPrimary ? (
                                  <Text size="xs" c="dimmed" opacity={0.88} lineClamp={2} mt={2}>
                                    {planPrimary}
                                    {planDetail ? ` · ${planDetail}` : ''}
                                  </Text>
                                ) : shiftBackend?.planned_end_local ? (
                                  <Text size="xs" c="dimmed" opacity={0.88} lineClamp={2} mt={2}>
                                    Плановая смена до {shiftBackend.planned_end_local}
                                  </Text>
                                ) : null}
                              </Box>
                            </Group>
                            {onFact.length > 0 ? (
                              <Divider label="Коллеги" labelPosition="center" my="sm" />
                            ) : null}
                          </>
                        ) : null}
                        {onFact.length > 0
                          ? onFact.map((row) => <ColleagueOnShiftLine key={row.user_id} row={row} />)
                          : !shift.active ? (
                              <Text size="xs" c="dimmed">
                                Никого по факту открытой смены.
                              </Text>
                            ) : null}
                      </>
                    )
                  })()}
                </Stack>
              </ScrollArea.Autosize>
            )}
        </Popover.Dropdown>
      </Popover>

      <Menu shadow="md" width={260} position="bottom-end" withArrow withinPortal>
        <Menu.Target>
          <UnstyledButton
            aria-label="Меню профиля"
            aria-haspopup="menu"
            style={{ lineHeight: 0, borderRadius: 'var(--mantine-radius-md)' }}
          >
            <Avatar
              src={avatarSrc}
              size={30}
              radius="sm"
              color="brand"
              variant="light"
              onError={() => setAvatarBroken(true)}
            >
              {!avatarSrc ? initialsFromUser(user) : null}
            </Avatar>
          </UnstyledButton>
        </Menu.Target>
        <Menu.Dropdown>
          <Box px="sm" py="xs">
            <Group gap="sm" wrap="nowrap" align="flex-start">
              <Avatar
                src={avatarSrc}
                size={44}
                radius="md"
                color="brand"
                variant="light"
                style={{ flexShrink: 0 }}
                onError={() => setAvatarBroken(true)}
              >
                {!avatarSrc ? initialsFromUser(user) : null}
              </Avatar>
              <Box style={{ flex: 1, minWidth: 0 }}>
                <Text size="sm" fw={600} lh={1.35} lineClamp={2}>
                  {displayName(user)}
                </Text>
                <Text size="xs" c="dimmed" truncate mt={2}>
                  {roleLine1}
                </Text>
                {roleLine2 ? (
                  <Text size="xs" c="dimmed" opacity={0.85} truncate>
                    {roleLine2}
                  </Text>
                ) : null}
              </Box>
            </Group>
          </Box>
          <Menu.Divider />
          <Menu.Item
            leftSection={<IconUser size={16} stroke={1.5} />}
            onClick={() => openOrSelectTab('/profile')}
          >
            Перейти в профиль
          </Menu.Item>
          <Menu.Item
            color="red"
            leftSection={<IconLogout size={16} stroke={1.5} />}
            onClick={() =>
              openConfirmModal({
                title: 'Завершить работу и выйти?',
                children: (
                  <Text size="sm" c="dimmed">
                    Подтвердите завершение работы и выход из системы.
                  </Text>
                ),
                labels: { confirm: 'Выйти', cancel: 'Отмена' },
                confirmProps: { color: 'red' },
                onConfirm: () => onSignOut(),
              })
            }
          >
            Выйти
          </Menu.Item>
        </Menu.Dropdown>
      </Menu>
    </Group>
  )
}
